/**
 * Linked-Views — shared selection state + flash animation for the card-GUI.
 *
 *
 * Public API:
 *   createLinkedViews({ cardListEl, overlayContainerEl, onPdfPageScroll }) → LinkedViewsHandle
 *
 * Selection modes:
 *   "single"  — replace selection with [id]; set anchor to id.
 *   "toggle"  — add or remove id; preserve other selected ids; anchor unchanged.
 *   "range"   — span from anchor (or first elem if no anchor) to id, inclusive,
 *               using `cardListEl` document order; replaces prior selection;
 *               anchor unchanged.
 *
 * Flash animation: we set a CSS class for ~0.85s; `prefers-reduced-motion`
 * skips the animation but still scrolls into view.
 *
 * @module linked-views
 */
const FLASH_MS = 850;
export function createLinkedViews(opts = {}) {
    const { cardListEl, overlayContainerEl, onPdfPageScroll, 
    // WHY: inject browser globals so tests don't need globalThis monkeypatching.
    // Production callers omit these; globals are resolved lazily via typeof guards.
    doc: docOpt, matchMedia: matchMediaOpt, cssEscape: cssEscapeOpt, } = opts;
    // Resolve globals lazily (once per createLinkedViews call) so production
    // callers that omit the overrides get the real browser globals, while Node
    // tests can inject stubs without triggering ReferenceErrors.
    const _doc = docOpt !== undefined
        ? docOpt
        : (typeof document !== "undefined" ? document : null);
    const _matchMedia = matchMediaOpt !== undefined
        ? matchMediaOpt
        : (typeof window !== "undefined" && window.matchMedia ? window.matchMedia.bind(window) : null);
    const _cssEscape = cssEscapeOpt !== undefined
        ? cssEscapeOpt
        : (typeof CSS !== "undefined" && CSS.escape ? CSS.escape.bind(CSS) : null);
    const selectedIds = new Set();
    let anchorId = null;
    let renderHook = null;
    function setRenderHook(fn) { renderHook = fn; }
    function getSelected() { return new Set(selectedIds); }
    function getAnchor() { return anchorId; }
    function clearSelection() {
        selectedIds.clear();
        anchorId = null;
        applyClasses();
        if (typeof renderHook === "function")
            renderHook(getSelected());
    }
    function selectStructId(id, mode = "single") {
        if (!id)
            return;
        if (mode === "toggle") {
            if (selectedIds.has(id))
                selectedIds.delete(id);
            else
                selectedIds.add(id);
            // anchor stays — Cmd/Ctrl+click does NOT move it (Finder semantics).
        }
        else if (mode === "range") {
            // Build range from anchor to id using card-list document order.
            const ids = orderedCardIds();
            const ti = ids.indexOf(id);
            if (ti < 0) {
                selectedIds.clear();
                selectedIds.add(id);
            }
            else {
                let ai = anchorId ? ids.indexOf(anchorId) : 0;
                if (ai < 0)
                    ai = 0;
                const lo = Math.min(ai, ti);
                const hi = Math.max(ai, ti);
                selectedIds.clear();
                // WHY: use slice+forEach so noUncheckedIndexedAccess doesn't widen elements to T|undefined
                ids.slice(lo, hi + 1).forEach(x => selectedIds.add(x));
            }
            // anchor stays — only single-click moves the anchor (Finder).
        }
        else {
            // "single" — replace + set anchor.
            selectedIds.clear();
            selectedIds.add(id);
            anchorId = id;
        }
        applyClasses();
        if (typeof renderHook === "function")
            renderHook(getSelected());
    }
    /**
     * Bulk-replace the selection set. Used by Cmd-A select-all and by the
     * Esc-then-rebuild paths in the right-click menu.
     */
    function setSelection(idSet, opts) {
        const keepAnchor = !!(opts && opts.keepAnchor);
        selectedIds.clear();
        if (idSet) {
            for (const x of idSet)
                if (x)
                    selectedIds.add(x);
        }
        if (!keepAnchor) {
            // WHY: selectedIds.size === 1 guarantees the first element exists; use .values().next() to avoid T|undefined
            anchorId = selectedIds.size === 1 ? (selectedIds.values().next().value ?? null) : null;
        }
        applyClasses();
        if (typeof renderHook === "function")
            renderHook(getSelected());
    }
    /**
     * Read the card-list children's structIds in document order. Returns
     * an empty array if cardListEl is missing.
     *
     * Lives on linkedViews because both `range` mode (above) and external
     * callers (multi-select.js for Cmd-A, context menus) need the same
     * ordering — single source of truth.
     */
    function orderedCardIds() {
        if (!cardListEl || typeof cardListEl.querySelectorAll !== "function")
            return [];
        const out = [];
        for (const c of cardListEl.querySelectorAll(".editor-card")) {
            if (c.dataset && c.dataset.structId)
                out.push(c.dataset.structId);
        }
        return out;
    }
    function applyClasses() {
        const isMulti = selectedIds.size > 1;
        if (cardListEl && typeof cardListEl.querySelectorAll === "function") {
            for (const c of cardListEl.querySelectorAll(".editor-card")) {
                const id = c.dataset.structId;
                const isSel = id ? selectedIds.has(id) : false;
                c.classList.toggle("is-selected", isSel);
                // multi-selected adds the secondary "part of a group" treatment.
                c.classList.toggle("is-multi-selected", isSel && isMulti);
            }
        }
        // Multi-page mode renders overlays into per-page `.editor-pdf-overlay`
        // containers, not into the configured legacy `overlayContainerEl`.
        // Walk both: the legacy container (for single-page path), and every
        // per-page container globally. Without this fallback, range/toggle
        // selection on overlays in multi-page mode produces no visual change.
        const seen = new WeakSet();
        if (overlayContainerEl && typeof overlayContainerEl.querySelectorAll === "function") {
            for (const o of overlayContainerEl.querySelectorAll(".editor-overlay-bbox")) {
                seen.add(o);
                const id = o.dataset.structId;
                const isSel = id ? selectedIds.has(id) : false;
                o.classList.toggle("is-selected", isSel);
                o.classList.toggle("is-multi-selected", isSel && isMulti);
            }
        }
        if (_doc && typeof _doc.querySelectorAll === "function") {
            for (const o of _doc.querySelectorAll(".editor-overlay-bbox")) {
                if (seen.has(o))
                    continue;
                const id = o.dataset.structId;
                const isSel = id ? selectedIds.has(id) : false;
                o.classList.toggle("is-selected", isSel);
                o.classList.toggle("is-multi-selected", isSel && isMulti);
            }
        }
    }
    function flashCard(id) {
        if (!cardListEl)
            return;
        const card = findEl(cardListEl, ".editor-card", id, _cssEscape);
        if (!card)
            return;
        applyFlash(card, "is-flash-card");
    }
    function flashOverlay(id) {
        // Multi-page mode renders overlays into per-page `.editor-pdf-overlay`
        // containers under #editor-pdf-pages, not into the legacy single-page
        // `overlayContainerEl` we got at construction. Search BOTH so the
        // flash fires regardless of which path rendered the overlay. Without
        // this fallback, the card-jump flash is silently a no-op in multi-
        // page mode (the user sees nothing happen).
        const ov = findOverlayAnywhere(id);
        if (!ov)
            return;
        applyFlash(ov, "is-flash-overlay");
    }
    /**
     * Locate an overlay element by structId. Tries the configured
     * `overlayContainerEl` first (legacy single-page path) then falls back
     * to a workspace-wide search across all per-page overlay containers.
     */
    function findOverlayAnywhere(id) {
        if (!id)
            return null;
        if (overlayContainerEl) {
            const ov = findEl(overlayContainerEl, ".editor-overlay-bbox", id, _cssEscape);
            if (ov)
                return ov;
        }
        if (!_doc)
            return null;
        const escaped = _cssEscape
            ? _cssEscape(id)
            : String(id).replace(/(["\\])/g, "\\$1");
        return _doc.querySelector(`.editor-overlay-bbox[data-struct-id="${escaped}"]`);
    }
    function applyFlash(el, cls) {
        const prefersReduced = _matchMedia
            ? _matchMedia("(prefers-reduced-motion: reduce)").matches
            : false;
        if (prefersReduced)
            return; // selection class already gives a strong cue
        el.classList.remove(cls);
        // Force reflow so re-adding the class restarts the animation
        // (no-op in jsdom; harmless in browsers without DOM lookups elsewhere).
        // eslint-disable-next-line no-unused-expressions
        void (el.offsetWidth);
        el.classList.add(cls);
        setTimeout(() => el.classList.remove(cls), FLASH_MS);
    }
    function scrollCardIntoView(id) {
        if (!cardListEl)
            return;
        const card = findEl(cardListEl, ".editor-card", id, _cssEscape);
        if (!card || typeof card.scrollIntoView !== "function")
            return;
        const prefersReduced = _matchMedia
            ? _matchMedia("(prefers-reduced-motion: reduce)").matches : false;
        card.scrollIntoView({ block: "nearest", behavior: prefersReduced ? "auto" : "smooth" });
    }
    function scrollOverlayIntoView(id, page) {
        if (typeof onPdfPageScroll === "function" && page) {
            onPdfPageScroll(page);
        }
        const ov = findOverlayAnywhere(id);
        if (!ov || typeof ov.scrollIntoView !== "function")
            return;
        const prefersReduced = _matchMedia
            ? _matchMedia("(prefers-reduced-motion: reduce)").matches : false;
        ov.scrollIntoView({ block: "nearest", inline: "nearest", behavior: prefersReduced ? "auto" : "smooth" });
    }
    /**
     * Wire click on an overlay → flash card + scroll-into-view.
     */
    function attachOverlayClick(overlayEl, structId, _page) {
        if (!overlayEl)
            return;
        overlayEl.addEventListener("click", (ev) => {
            ev.stopPropagation();
            const mode = ev.shiftKey ? "range" : ((ev.metaKey || ev.ctrlKey) ? "toggle" : "single");
            selectStructId(structId, mode);
            // Only scroll/flash when this becomes the most-recently-added
            // single-pane focus; on shift/cmd we'd flash the whole group, which
            // is noisy. UX memo §7.1: flash fires for the most-recent only.
            if (mode === "single") {
                scrollCardIntoView(structId);
                flashCard(structId);
            }
            const card = cardListEl && findEl(cardListEl, ".editor-card", structId, _cssEscape);
            if (card && typeof card.focus === "function") {
                try {
                    card.focus({ preventScroll: true });
                }
                catch { /* older browsers throw on options arg; safe to ignore */ }
            }
        });
        overlayEl.addEventListener("keydown", (ev) => {
            if (ev.key === "Enter" || ev.key === " ") {
                ev.preventDefault();
                selectStructId(structId, "single");
                scrollCardIntoView(structId);
                flashCard(structId);
            }
        });
    }
    /**
     * Wire click on a card → flash overlay + scroll PDF to its page.
     */
    function attachCardClick(cardEl, structId, page) {
        if (!cardEl)
            return;
        // Card body click is wired in cards.js (calls onSelect); this helper
        // is for direct-click scenarios (tests, programmatic).
        cardEl.addEventListener("click", (ev) => {
            // Ignore interactive descendants — same logic as cards.js so
            // double-wiring doesn't break expand/collapse.
            const t = ev.target;
            if (t && t.closest && (t.closest(".editor-card-typechip") ||
                t.closest(".editor-card-jump-btn") ||
                t.closest(".editor-card-issue-badge") ||
                t.closest(".editor-card-handle") ||
                t.closest(".editor-card-fields"))) {
                return;
            }
            const mode = ev.shiftKey ? "range" : ((ev.metaKey || ev.ctrlKey) ? "toggle" : "single");
            selectStructId(structId, mode);
            if (mode === "single") {
                scrollOverlayIntoView(structId, page);
                flashOverlay(structId);
            }
        });
    }
    return {
        selectStructId,
        setSelection,
        clearSelection,
        getSelected,
        getAnchor,
        orderedCardIds,
        flashCard,
        flashOverlay,
        scrollCardIntoView,
        scrollOverlayIntoView,
        attachCardClick,
        attachOverlayClick,
        applyClasses,
        setRenderHook,
    };
}
/**
 * Find an element by CSS selector + structId data attribute.
 *
 * @internal — module-private helper, not exported.
 */
function findEl(root, selector, structId, cssEscape) {
    if (!root || !root.querySelector || !structId)
        return null;
    // WHY: prefer injected cssEscape over globalThis.CSS so tests can inject
    // a stub without monkeypatching globals.
    const escFn = cssEscape
        || (typeof CSS !== "undefined" && CSS.escape ? CSS.escape.bind(CSS) : null);
    const escaped = escFn
        ? escFn(structId)
        : String(structId).replace(/(["\\])/g, "\\$1");
    return root.querySelector(`${selector}[data-struct-id="${escaped}"]`);
}
